import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Chip,
  Card,
  CardMedia,
  Divider,
  useMediaQuery,
  useTheme,
  Dialog,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";

import styles from "./GranthDetail.module.scss";

import {
  Author,
  Loader,
  SocialShare,
  ShareButton,
} from "../../../library/common";
import { PdfReader } from "../../../library/component";

import { axiosInstance } from "../../../services";
import localKeys from "../../../locales/localesKeys.json";

import {
  getGoogleDriveDirectLink,
  getCloudinaryCoverUrl,
  getCloudinaryDownloadUrl,
  getBookFallbackColor,
} from "../../../utils";

const GranthDetail = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [bookData, setBookData] = useState(null);
  const { id } = useParams();
  const [slug, setSlug] = useState(id);
  const location = useLocation();
  const isArticle = location.pathname.includes("/article");

  // PDF reader states
  const [pdfMode, setPdfMode] = useState(null); // null | "inline" | "modal"
  const [imageSrc, setImageSrc] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const bookDate = new Date(bookData?.publishDate);
  const presentDate = new Date();
  const publishDate = bookDate.getTime() ? bookDate.toDateString() : "";
  const isBookAvailable = bookDate < presentDate;

  useEffect(() => {
    if (id !== slug) setSlug(id);
  }, [id, slug]);

  useEffect(() => {
    if (slug) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Reset reader and dropdown when navigating between books
  useEffect(() => {
    setPdfMode(null);
    setDropdownOpen(false);
  }, [slug]);

  // Click-away listener for dropdown
  useEffect(() => {
    const handleOutsideClick = () => {
      setDropdownOpen(false);
    };
    if (dropdownOpen) {
      document.addEventListener("click", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (bookData) {
      setImageSrc(
        bookData?.image && bookData.image !== "null"
          ? bookData.image
          : getCloudinaryCoverUrl(bookData?.fileUrl) || ""
      );
    }
  }, [bookData]);

  const fetchData = async () => {
    const response = await axiosInstance.get(`/books/${slug}`);
    setBookData(response?.data);
  };

  const handleDownload = async () => {
    if (!bookData?.fileUrl) return;
    const downloadUrl = getCloudinaryDownloadUrl(bookData.fileUrl);
    const directLink = getGoogleDriveDirectLink(downloadUrl);

    // Google Drive direct download open
    const isGoogleDrive = downloadUrl.includes("drive.google.com") || downloadUrl.includes("docs.google.com");
    if (isGoogleDrive) {
      window.open(directLink, "_blank");
      return;
    }

    try {
      const response = await fetch(downloadUrl, {
        mode: "cors",
      });
      if (!response.ok) throw new Error("File fetch failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = bookTitle.replace(/\s+/g, "_") + ".pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Fallback: open direct link in a new tab
      window.open(directLink, "_blank");
    }
  };

  const bookTitle = bookData?.title?.[language] || bookData?.title?.en || "";
  const hasFile = bookData?.fileUrl && bookData.fileUrl !== "null";

  return (
    <div className={styles.container}>
      {!bookData ? (
        <Loader />
      ) : (
        <>
          <div className={styles.bookWrapper}>
            {/* ── Left column ── */}
            <div className={styles.leftColumn}>
              <Card
                elevation={isMobile ? 1 : 3}
                className={styles.imageContainer}
              >
                {imageSrc ? (
                  <CardMedia
                    component="img"
                    className={styles.coverImage}
                    image={imageSrc}
                    alt={bookTitle}
                    loading="lazy"
                    onError={() => {
                      const pdfBackup = getCloudinaryCoverUrl(bookData?.fileUrl);
                      if (pdfBackup && imageSrc !== pdfBackup) {
                        setImageSrc(pdfBackup);
                      } else {
                        setImageSrc(""); // Set to empty to trigger fallback text cover
                      }
                    }}
                  />
                ) : (
                  <div
                    className={isArticle ? styles.articleFallbackCover : styles.detailFallbackCover}
                    style={
                      isArticle
                        ? {}
                        : {
                            backgroundColor: getBookFallbackColor(bookTitle),
                          }
                    }
                  >
                    <div className={styles.fallbackBookIcon}>
                      {isArticle ? "📜" : "📖"}
                    </div>
                    <h2 className={styles.fallbackTitle}>{bookTitle}</h2>
                  </div>
                )}
              </Card>

              {/* Action buttons */}
              {isBookAvailable ? (
                <div className={styles.downloadBtnWrapper}>
                  {hasFile ? (
                    <>
                      {/* Split button with dropdown option for inline read */}
                      <div className={styles.readSplitBtn}>
                        <button
                          className={`${styles.actionBtn} ${styles.readBtn}`}
                          onClick={() => setPdfMode("modal")}
                        >
                          <MenuBookIcon sx={{ fontSize: 18 }} />
                          {t(localKeys.GRANT_BUTTON_READ)}
                        </button>
                        <button
                          className={styles.dropdownToggle}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDropdownOpen(!dropdownOpen);
                          }}
                          aria-haspopup="true"
                          aria-expanded={dropdownOpen}
                          aria-label="Read options dropdown"
                        >
                          ▼
                        </button>
                        {dropdownOpen && (
                          <div className={styles.dropdownMenu}>
                            <button
                              className={styles.dropdownItem}
                              onClick={() => {
                                setPdfMode("modal");
                                setDropdownOpen(false);
                              }}
                            >
                              {t(localKeys.GRANT_BUTTON_FULLSCREEN)}
                            </button>
                            <button
                              className={styles.dropdownItem}
                              onClick={() => {
                                setPdfMode(pdfMode === "inline" ? null : "inline");
                                setDropdownOpen(false);
                              }}
                            >
                              {pdfMode === "inline"
                                ? t(localKeys.GRANT_BUTTON_CLOSE_INLINE)
                                : t(localKeys.GRANT_BUTTON_INLINE_READER)}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Download */}
                      <button
                        className={`${styles.actionBtn} ${styles.downloadBtn}`}
                        onClick={handleDownload}
                      >
                        <DownloadIcon sx={{ fontSize: 18 }} />
                        {t(localKeys.GRANT_BUTTON_DOWNLOAD)}
                      </button>
                    </>
                  ) : (
                    <Typography className={styles.bookUnavailableMssg}>
                      {t(localKeys.BOOKFILENOTFOUND)}
                    </Typography>
                  )}
                </div>
              ) : (
                <Typography className={styles.bookUnavailableMssg}>
                  {t(localKeys.BOOKNOTFOUND)} <br />(
                  {t(localKeys.AVAILABLEFROM)} {publishDate})
                </Typography>
              )}

              {/* ── Inline PDF Reader ── */}
              {pdfMode === "inline" && hasFile && (
                <div className={styles.inlineReaderWrapper}>
                  <PdfReader
                    fileUrl={bookData.fileUrl}
                    title={bookTitle}
                    isModal={false}
                    onClose={() => setPdfMode(null)}
                    onDownload={handleDownload}
                    openUrl={bookData.fileUrl}
                  />
                </div>
              )}
            </div>

            {/* ── Right column ── */}
            <div className={`${styles.rightColumn} ql-editor`}>
              <div className={styles.header}>
                <div>
                  <Typography className={styles.seriesTitle}>
                    {t(localKeys.PART)} {bookData?.partNo}{" "}
                    {bookData?.languageId?.name?.[language]
                      ? `(${bookData?.languageId?.name?.[language]})`
                      : ""}
                  </Typography>

                  <div className={styles.titleAndShare}>
                    <Typography className={styles.bookTitle}>
                      {bookTitle}
                    </Typography>
                    <ShareButton
                      title={bookData?.metafields?.title || ""}
                      description={bookData?.metafields?.description || ""}
                    />
                  </div>

                  <Author author={bookData?.author} showInCard />
                </div>

                <SocialShare
                  socialMediaLinks={bookData?.socialMediaLinks}
                  wrapperClass={styles.socialContainer}
                  title={bookTitle}
                  isSocialPage={false}
                />
              </div>

              {bookData?.category?.length > 0 && (
                <div className={styles.awardsSection}>
                  {bookData?.category?.map((item) => (
                    <Chip
                      label={
                        item?.name?.[language] ? item?.name?.[language] : ""
                      }
                      className={`${styles.awardChip} ${styles.primary}`}
                      size={isMobile ? "small" : "medium"}
                      key={item?._id}
                    />
                  ))}
                </div>
              )}

              <div className="ql-editor">
                <Typography
                  className={`${styles.description}`}
                  dangerouslySetInnerHTML={{
                    __html: bookData?.longDescription?.[language]
                      ? bookData?.longDescription?.[language]
                      : "",
                  }}
                ></Typography>
              </div>

              <div className={styles.genresSection}>
                <Typography className={styles.genreTitle} variant="subtitle2">
                  {t(localKeys.GENRES)}
                </Typography>
                <div className={styles.genreChips}>
                  {bookData?.sector?.map((genre) => (
                    <Chip
                      key={genre}
                      label={
                        genre?.name?.[language] ? genre?.name?.[language] : ""
                      }
                      className={styles.genreChip}
                      size={isMobile ? "small" : "medium"}
                    />
                  )) || "Religion"}
                </div>
              </div>

              <Divider className={styles.divider} />

              <div className={styles.details}>
                <Typography className={styles.pageInfo}>
                  {bookData?.noOfPages || "N/A"} {t(localKeys.PAGES)}
                </Typography>
                {publishDate ? (
                  <Typography className={styles.publishInfo}>
                    {t(localKeys.FIRSTPUBLISHED)} {publishDate}
                  </Typography>
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Fullscreen / Modal PDF Reader ── */}
          <Dialog
            open={pdfMode === "modal"}
            onClose={() => setPdfMode(null)}
            fullScreen
            PaperProps={{
              sx: {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }}
          >
            <PdfReader
              fileUrl={bookData?.fileUrl}
              title={bookTitle}
              isModal
              onClose={() => setPdfMode(null)}
              onDownload={handleDownload}
              openUrl={bookData?.fileUrl}
            />
          </Dialog>
        </>
      )}
    </div>
  );
};

export default GranthDetail;
